import React from "react";
import {
  Box,
  Button,
  Divider,
  Group,
  Loader,
  Stack,
  Text,
  useMantineTheme,
} from "@mantine/core";
import {
  IconCamera,
  IconCheck,
  IconLock,
  IconSparkles,
} from "@tabler/icons-react";

/**
 * Bottom drawer overlay for the age-estimation capture flow.
 *
 * Layout (top to bottom):
 *   [shield icon] Title + subtitle           ← header (paused only)
 *   ─────────────────────                    ← divider (paused only)
 *   ✨ Section header                         ← e.g. "Be ready…"
 *   [icon] [icon] [icon]                     ← prep checks row
 *   labels         labels         labels
 *   (status bubbles only when showIndicators)
 *   [🔒] Camera access is only used for …    ← permission callout
 *   [📷 Allow Camera Access]                 ← primary CTA
 *
 * Icons inside the check row sit in tinted primary-color circles
 * for visual weight; labels and status bubbles render underneath.
 * Status bubbles only show when `showIndicators` is true (i.e. once
 * the camera is live and the SDK is reporting real per-check state).
 *
 * Purely presentational. No context, no hooks beyond theme.
 */
const AgeEstimationStartDrawer = ({
  title,
  subtitle,
  sectionHeader,
  checks = [],
  showIndicators = true,
  // When true, switches the drawer into the live-capture HUD
  // treatment — dark translucent background, lighter text, less
  // padding, smaller icons, no top header. Use for live capture.
  compact = false,
  // Optional permission callout above the button — gray pill with
  // a lock icon, explicitly naming what the camera permission is
  // being used for. Pass `null`/omit to hide it (e.g. in live state).
  permissionCallout,
  // Small privacy reassurance line shown at the bottom of the
  // drawer in the live state ("Your image is used only to verify
  // your age.") — lock-icon prefix, smaller and subtler than the
  // permissionCallout box. Pass `null`/omit to hide it.
  privacyCallout,
  showButton = true,
  buttonLabel,
  preparingLabel = "Getting ready…",
  modelsReady = true,
  disabled = false,
  // Text/icon color of the filled CTA (styles.components.button.color).
  // The icon and the preparing spinner follow it instead of hardcoding
  // white, so a customer with a light CTA and dark text gets a dark icon.
  buttonTextColor = "#fff",
  onStart,
  footer,
  // Ref to the drawer's root box, so a parent can measure how much
  // of the viewport it covers (it's position: fixed, so a wrapper
  // element can't be measured instead).
  innerRef,
  // Landscape phone: dock as a full-height panel on the right instead
  // of a card across the bottom (which would cover most of a sideways
  // screen). The prep content scrolls if it has to; the button and
  // footer stay pinned to the panel's bottom, like the consent screen.
  side = false,
}) => {
  const theme = useMantineTheme();
  const isDark = theme.colorScheme === "dark";
  // Compact (live-capture HUD) treatment: dark translucent panel
  // overlaid on the live video, with lighter text and tighter
  // padding. Structured (paused) treatment: white card with full
  // structured header + section + callout + button.
  const darkSurface = compact || isDark;
  const drawerBg = compact
    ? "rgba(22,22,24,0.88)"
    : isDark
    ? theme.colors.dark[6]
    : "#fff";
  const titleColor = darkSurface ? theme.white : theme.black;
  const subtitleColor = darkSurface
    ? theme.colors.gray[4]
    : theme.colors.gray[7];
  const labelColor = darkSurface
    ? theme.colors.gray[2]
    : theme.colors.gray[7];
  const idleRingColor = darkSurface
    ? theme.colors.dark[3]
    : theme.colors.gray[3];
  // Success color is reserved for "this check is satisfied" so it
  // doesn't fight with the primary-color active-glow that rings
  // the oval during live capture.
  const successColor = theme.colors.green[6];
  const separatorColor = darkSurface
    ? "rgba(255,255,255,0.12)"
    : theme.colors.gray[2];
  const primary6 =
    theme.colors[theme.primaryColor]?.[6] || theme.colors.teal[6];
  const primary0 =
    theme.colors[theme.primaryColor]?.[0] || theme.colors.gray[0];
  const calloutBg = darkSurface
    ? "rgba(255,255,255,0.04)"
    : theme.colors.gray[1];
  const calloutIconBg = darkSurface
    ? "rgba(255,255,255,0.08)"
    : theme.colors.gray[3];
  const calloutIconColor = darkSurface
    ? theme.colors.gray[3]
    : theme.colors.gray[7];
  const dividerColor = darkSurface
    ? theme.colors.dark[4]
    : theme.colors.gray[2];
  const checkIconBg = compact
    ? "transparent"
    : isDark
    ? "rgba(255,255,255,0.06)"
    : primary0;
  // Paused-mode prep icons render in a tinted pill — the glyph
  // itself goes black on light surfaces and white on dark, so
  // the icon reads as foreground content rather than an accent.
  // Compact/live mode keeps primary because those icons sit on
  // a dark translucent backdrop where black would disappear.
  const checkIconGlyphColor = isDark ? theme.white : theme.black;

  // Landscape intro panel: height is scarce, so the prep icons shrink
  // and the permission callout drops to a single line above the button.
  const sidePaused = side && !compact;
  const INDICATOR_SIZE = compact ? 18 : 22;
  const CHECK_ICON_CIRCLE = compact ? 0 : sidePaused ? 44 : 64;
  const CHECK_ICON_SIZE = compact ? 30 : 32;
  const drawerPadding =
    compact && side
      ? "14px 14px"
      : sidePaused
      ? "18px 18px 16px"
      : compact
      ? "16px 18px 14px"
      : "26px 22px 28px";

  const body = (
    <>
      {title && (
        <Stack spacing={2} align="center">
          <Text
            fw={800}
            fz={21}
            lh={1.2}
            c={titleColor}
            align="center"
            // Tight tracking keeps a slightly larger headline
            // on a single line at typical mobile widths.
            // pre-line still respected for any intentional
            // `\n` in custom locale copy.
            style={{
              letterSpacing: "-0.015em",
              whiteSpace: "pre-line",
            }}
          >
            {title}
          </Text>
          {subtitle && (
            <Text fz={13} lh={1.35} c={subtitleColor} align="center">
              {subtitle}
            </Text>
          )}
        </Stack>
      )}

      {sectionHeader && (
        <Group spacing={8} noWrap>
          <IconSparkles size={18} stroke={2} color={primary6} />
          <Text fw={700} fz={15} c={titleColor}>
            {sectionHeader}
          </Text>
        </Group>
      )}

      {checks.length > 0 && side && compact && (
        // Landscape live HUD: the checks stack as rows in a narrow
        // column so the oval can stay centered on the frame.
        <Stack spacing={10}>
          {checks.map((c) => {
            const Icon = c.icon;
            const lit = !!c.satisfied;
            return (
              <Group key={c.key || c.label} spacing={10} noWrap>
                <Icon size={24} stroke={1.8} color={primary6} style={{ flexShrink: 0 }} />
                <Text
                  fz={13}
                  fw={500}
                  c={labelColor}
                  lh={1.2}
                  style={{ flex: 1, minWidth: 0 }}
                >
                  {/* Row layout: the column-layout line breaks would
                      only waste height here. */}
                  {String(c.label).replace(/\n/g, " ")}
                </Text>
                {showIndicators && (
                  <Box
                    style={{
                      flexShrink: 0,
                      width: INDICATOR_SIZE,
                      height: INDICATOR_SIZE,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: lit ? successColor : "transparent",
                      border: lit
                        ? `1px solid ${successColor}`
                        : `2px solid ${idleRingColor}`,
                      transition:
                        "background-color 250ms ease, border-color 250ms ease",
                    }}
                  >
                    {lit && <IconCheck size={12} stroke={3} color="#fff" />}
                  </Box>
                )}
              </Group>
            );
          })}
        </Stack>
      )}

      {checks.length > 0 && !(side && compact) && (
        <Group position="apart" spacing={0} noWrap grow align="stretch">
          {checks.map((c, idx) => {
            const Icon = c.icon;
            const lit = !!c.satisfied;
            return (
              <React.Fragment key={c.key || c.label}>
                <Stack
                  spacing={compact ? 6 : 8}
                  align="center"
                  style={{ paddingInline: 4 }}
                >
                  {compact ? (
                    <Icon
                      size={CHECK_ICON_SIZE}
                      stroke={1.8}
                      color={primary6}
                    />
                  ) : (
                    <Box
                      style={{
                        width: CHECK_ICON_CIRCLE,
                        height: CHECK_ICON_CIRCLE,
                        borderRadius: "50%",
                        backgroundColor: checkIconBg,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Icon
                        size={sidePaused ? 24 : 32}
                        stroke={1.8}
                        color={checkIconGlyphColor}
                      />
                    </Box>
                  )}
                  <Text
                    fz={sidePaused ? 12 : 14}
                    fw={500}
                    c={labelColor}
                    align="center"
                    lh={1.2}
                    // Honor explicit `\n` line breaks in labels
                    // so the three columns line up at a uniform
                    // two-row height regardless of word length.
                    style={{ whiteSpace: "pre-line" }}
                  >
                    {c.label}
                  </Text>
                  {showIndicators && (
                    <Box
                      style={{
                        width: INDICATOR_SIZE,
                        height: INDICATOR_SIZE,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: lit ? successColor : "transparent",
                        border: lit
                          ? `1px solid ${successColor}`
                          : `2px solid ${idleRingColor}`,
                        transition:
                          "background-color 250ms ease, border-color 250ms ease",
                      }}
                    >
                      {lit && (
                        <IconCheck size={14} stroke={3} color="#fff" />
                      )}
                    </Box>
                  )}
                </Stack>
                {idx < checks.length - 1 && (
                  <Box
                    style={{
                      width: 1,
                      alignSelf: "stretch",
                      backgroundColor: separatorColor,
                      flex: "0 0 auto",
                    }}
                  />
                )}
              </React.Fragment>
            );
          })}
        </Group>
      )}

      {permissionCallout && !sidePaused && (
        <Group
          noWrap
          spacing={12}
          align="center"
          style={{
            backgroundColor: calloutBg,
            borderRadius: 12,
            padding: "12px 16px",
          }}
        >
          <Box
            style={{
              flex: "0 0 auto",
              width: 32,
              height: 32,
              borderRadius: "50%",
              backgroundColor: calloutIconBg,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <IconLock size={17} stroke={2.2} color={calloutIconColor} />
          </Box>
          <Text fz={14} c={labelColor} lh={1.4} style={{ flex: 1 }}>
            {permissionCallout}
          </Text>
        </Group>
      )}

    </>
  );

  const actions = (
    <>
      {permissionCallout && sidePaused && (
        <Group spacing={6} noWrap position="center" align="flex-start">
          <IconLock
            size={14}
            stroke={2}
            color={calloutIconColor}
            style={{ flexShrink: 0, marginTop: 1 }}
          />
          <Text fz={12} c={labelColor} lh={1.35} align="center">
            {permissionCallout}
          </Text>
        </Group>
      )}
      {showButton && (
        <Button
          size="lg"
          onClick={onStart}
          fullWidth
          disabled={disabled || !modelsReady}
          leftIcon={
            !modelsReady ? (
              <Loader size="xs" color={buttonTextColor} />
            ) : (
              // currentColor keeps the glyph on the button's text color
              // in every state: the customer's button.color when set,
              // white by default, and Mantine's gray while disabled.
              // A literal color here becomes a stroke attribute on the
              // SVG and overrides the theme's "& svg" color rule.
              <IconCamera size={22} stroke={2} color="currentColor" />
            )
          }
        >
          {modelsReady ? buttonLabel : preparingLabel}
        </Button>
      )}

      {privacyCallout && (
        <Group spacing={8} noWrap position="center" align="center">
          <IconLock size={14} stroke={2} color={calloutIconColor} />
          <Text fz={12} c={labelColor} lh={1.3} align="center">
            {privacyCallout}
          </Text>
        </Group>
      )}

      {footer}
    </>
  );

  const spacing = compact ? "sm" : "md";
  const hasActions =
    showButton || !!privacyCallout || !!footer || (sidePaused && !!permissionCallout);

  return (
    <Box
      ref={innerRef}
      style={{
        position: "fixed",
        ...(side
          ? {
              right: 12,
              // Live HUD: a narrow column of check rows, vertically
              // centered, so the oval can stay centered on the frame.
              // The paused panel runs the full height so its button
              // sits at the bottom edge like every other screen's.
              ...(compact
                ? {
                    top: "50%",
                    transform: "translateY(-50%)",
                    maxHeight: "calc(100% - 24px)",
                  }
                : { top: 12, bottom: 12 }),
              width: compact ? 188 : "min(380px, 46vw)",
              display: "flex",
              flexDirection: "column",
            }
          : { left: 12, right: 12, bottom: 16 }),
        backgroundColor: drawerBg,
        padding: drawerPadding,
        borderRadius: compact ? 18 : 22,
        boxShadow: compact
          ? "0 -4px 20px rgba(0,0,0,0.4)"
          : "0 -10px 32px rgba(0,0,0,0.28)",
        backdropFilter: compact ? "blur(8px)" : undefined,
        WebkitBackdropFilter: compact ? "blur(8px)" : undefined,
        // One above VeratadCapture's z-9999 fullscreen portal.
        zIndex: 10000,
      }}
    >
      {side ? (
        <>
          <Box
            style={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Auto margins center the content while it fits and
                collapse to 0 when it doesn't, so it scrolls from the
                top. flexShrink 0 keeps the scroller from squashing
                the content to fit (which flattened the callout). */}
            <Stack spacing={spacing} style={{ margin: "auto 0", flexShrink: 0 }}>
              {body}
            </Stack>
          </Box>
          {hasActions && (
            <Stack spacing={10} style={{ flexShrink: 0, paddingTop: 12 }}>
              {actions}
            </Stack>
          )}
        </>
      ) : (
        <Stack spacing={spacing}>
          {body}
          {actions}
        </Stack>
      )}
    </Box>
  );
};

export default AgeEstimationStartDrawer;
